// Bidding Strategy Coherence — Bidding & Budgets section, LLM judgment row.
// Renders Claude's verdict on top with a collapsible "Campaigns Claude saw" table plus a
// compact strategy-distribution band beneath so the auditor can verify the verdict against
// the actual bidding mix.

import type { EvidenceProps } from './registry';
import { pct as formatPct, usdCents } from '../../utils/format';

interface CampaignRow {
  id?: string;
  name?: string;
  channelType?: string;
  biddingStrategyType?: string;
  targetCpaUsd: number | null;
  targetRoas: number | null;
  enhancedCpc: boolean | null;
}

interface BiddingShape {
  accountName: string;
  cid: string;
  campaignCount: number;
  distribution: Record<string, number>;
  campaigns: CampaignRow[];
}

// Friendly names for the bidding-strategy enum. Anything not in the map falls through to its
// raw enum string so we don't silently hide new Google enum values.
const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC',
  COMMISSION: 'Commission',
  MANUAL_CPM: 'Manual CPM',
  MANUAL_CPV: 'Manual CPV',
  UNKNOWN: 'Unknown',
  UNSPECIFIED: 'Unspecified',
  // Microsoft BiddingScheme.Type vocabulary (distinct strings from Google's, so one map serves
  // both platforms — this row shares a checkId across Google + Microsoft).
  TargetRoas: 'Target ROAS',
  TargetCpa: 'Target CPA',
  MaxConversionValue: 'Max Conv. Value',
  MaxConversions: 'Max Conversions',
  MaxClicks: 'Maximize Clicks',
  ManualCpc: 'Manual CPC',
  EnhancedCpc: 'Enhanced CPC',
  TargetImpressionShare: 'Target Imp. Share',
  MaxRoas: 'Max ROAS',
  InheritFromParent: 'Inherits (portfolio)',
  Commission: 'Commission',
  Unset: 'Not set'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

function formatTarget(c: CampaignRow): string {
  const parts: string[] = [];
  if (c.targetCpaUsd !== null && c.targetCpaUsd > 0) {
    parts.push(usdCents(c.targetCpaUsd));
  }
  if (c.targetRoas !== null && c.targetRoas > 0) {
    parts.push(formatPct(c.targetRoas));
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export const BiddingStrategyCoherenceEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as BiddingShape;

  // Distribution sorted by count desc; the leader sets the visual tone.
  const distEntries = Object.entries(e.distribution).sort((a, b) => b[1] - a[1]);
  const total = e.campaignCount;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Strategy distribution
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.campaignCount} enabled campaigns
          </span>
        </h4>
        <div className='bidding-distribution'>
          {distEntries.map(([strategy, count]) => {
            const pct = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={strategy} className='bidding-distribution__cell'>
                <div className='bidding-distribution__num'>{count.toLocaleString()}</div>
                <div className='bidding-distribution__lbl'>{strategyLabel(strategy)}</div>
                <div className='bidding-distribution__pct'>{pct.toFixed(0)}%</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Campaigns Claude saw
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.campaignCount} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--bidding'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Channel</th>
              <th>Strategy</th>
              <th className='evidence__th--right'>Target</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => (
              <tr key={c.id ?? c.name}>
                <td>{c.name ?? '(unnamed)'}</td>
                <td>{c.channelType ?? '—'}</td>
                <td>
                  {strategyLabel(c.biddingStrategyType)}
                  {c.enhancedCpc === true && c.biddingStrategyType === 'MANUAL_CPC' && (
                    <span className='evidence__inline-note'> + ECPC</span>
                  )}
                </td>
                <td className='evidence__td--right'>{formatTarget(c)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};
